import Licenses from "./Licenses/Licenses"
import { useCallback, useEffect, useRef, useState } from "react";
import InfoBar, { InfoMessageType } from "../InfoBar/InfoBar";
import Loader from "../Loader/Loader";
import { useGetLicensesInfo } from "src/Utils/Hooks/ManageLicenses/useGetLicensesInfo";
import { logger } from "src/Utils/LoggerService/LoggerService";
import { useInfobarTimeout } from "src/Utils/Hooks/InfobarTimeout/useInfobarTimeout";
import CloudStorage from "./CloudStorage/CloudStorage";
import NoLicenseActivated from "./NoLicenseActivated/NoLicenseActivated";
import LicensesUser from "./Licenses/LicensesUser";
import { useLocation } from "react-router-dom"; 
import PaymentSuccess from "./Payment/PaymentSuccess";
import { Config } from "src/Utils/ConfigService/LoadConfig";
import PaymentSupport from "./Payment/PaymentSupport/PaymentSupport";

import './LicensesAndStorage.css';
import { useEditPaymentMethod } from "src/Utils/Hooks/Payments/useEditPaymentMethod";
import { isDateExpired } from "src/Utils/DateExpiry/DateExpiryChecker";
import { useTranslation } from "react-i18next";
import UpgradeNonSuite from "./UpgradeNonSuite/UpgradeNonSuite";
import { useClinicContext } from "src/Utils/Hooks/ClinicContext";
import LicenseTitle from "./LicenseTitle/LicenseTitle";

const LicensesAndStorage = () => {
    const { t } = useTranslation();
    const {clinicInfo, currentUser} = useClinicContext();
    const isLicensePaymentEnabled = Config.GetConfigData()?.showLicensePayment ?? false;
    const isKolClinic = clinicInfo && clinicInfo.isKol;
    const showPaymentOption = isLicensePaymentEnabled && !isKolClinic;
    const { licensesInfo, licenseInfoError, licensesInfoLoading, getLicencesInfo } = useGetLicensesInfo();
    const { getEditPaymentInfo, data, loading, error } = useEditPaymentMethod();
    
    const [infoBar, setInfoBar] = useState<boolean>(false);
    const [editInfoBar, setEditInfoBar] = useState<boolean>(false);
    const [pageLoader, setPageLoader] = useState<boolean>(false);
    const [storageLoader, setStorageLoader] = useState<boolean>(false);

    const msgType = useRef<InfoMessageType>(InfoMessageType.none);
    const msg = useRef('');
    const { setInfobarTimeout } = useInfobarTimeout();
    const clinicId = useRef<string>('')

    // payment session id
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const paymentSessionId = queryParams.get('session_id');
    const [showPaymentSuccess, setShowPaymentSuccess] = useState<boolean>(!!paymentSessionId);
    useEffect(() => {
        setShowPaymentSuccess(!!paymentSessionId);
    }, [paymentSessionId]);
    const closePaymentSuccessMessage = () => {
        setShowPaymentSuccess(false);
        getLicencesInfo();
    }

    const updateUserLicenceList = useCallback(() => {
        getLicencesInfo();
    }, [getLicencesInfo])

    useEffect(() => {
        if (licensesInfo) {
            clinicId.current = licensesInfo.clinicId;
        }
    }, [licensesInfo])

    const showInfoBar = useCallback((message: string, messageType: InfoMessageType) => {
        if (messageType === InfoMessageType.none) {
            setInfoBar(false);
          return;
        }
    
        msg.current = message;
        msgType.current = messageType;
        setInfoBar(true);
        setInfobarTimeout(() => setInfoBar(false));
      }, [setInfobarTimeout]);

      const handleUserLicenseUpdate = useCallback((message: string, messageType: InfoMessageType) => {
        if (messageType === InfoMessageType.success) {
            updateUserLicenceList();
        }
        showInfoBar(message, messageType);
      }, [updateUserLicenceList, showInfoBar]);

    
      // Handle cloud storage info update
      const handleCloudStorageUpdate = useCallback((message: string, messageType: InfoMessageType) => {
        showInfoBar(message, messageType);
      }, [showInfoBar]);

    useEffect(() => {
        if (licenseInfoError !== '') {
            logger.logError(`Failed to fetch the license details`)
            handleUserLicenseUpdate(licenseInfoError, InfoMessageType.error);
        }
    }, [licenseInfoError, handleUserLicenseUpdate])

    useEffect(() => {
        setPageLoader(licensesInfoLoading || loading)
    }, [licensesInfoLoading, loading])

    const clinicHasUserAndStorageLicenses = () => licensesInfo && licensesInfo.clinicUserAndStorageLicenseInfo !== null;
    const clinicHasUserLicense = () => licensesInfo && licensesInfo.licenceInfo.length > 0;
    const clinicHasStorageLicense = () => licensesInfo && licensesInfo.clinicStorageLicense !== null;
    
    const containsSuiteLicense = () => typeof licensesInfo?.clinicUserAndStorageLicenseInfo?.licenseType === 'string' &&
        licensesInfo.clinicUserAndStorageLicenseInfo.licenseType.toLowerCase().includes('suite');


    const checkLicenseLicense = (type:string) => {
    if (!licensesInfo) return false;
        return licensesInfo?.clinicUserAndStorageLicenseInfo?.licenseType.includes(type) || 
               licensesInfo?.clinicStorageLicense?.licenseType.includes(type) ||
               licensesInfo?.licenceInfo?.some(license =>
                   license.licenseType.includes(type) || license.licenseType.includes(type));
    }

    const isSuiteLicense = () => {
        return checkLicenseLicense('Suite');
    }

    const isPermanentLicense = () => {
        return checkLicenseLicense('Permanent');
    }

    const isSubscriptionLicense = () => {
        return checkLicenseLicense('Subscription');
    }

    // Edit Payment Method InfoBar Logic
    const handleClickLink = () => {
        window.open(data?.paymentMethodUpdateUrl, '_self');
    }
    const isPaymentSupoortEnabled = () => {
           return showPaymentOption && licensesInfo && (isSuiteLicense() || isPermanentLicense())
    }

    useEffect(() => {
        if (licensesInfo?.clinicUserAndStorageLicenseInfo?.licenseEndDate) {
            if (isDateExpired(licensesInfo.clinicUserAndStorageLicenseInfo.licenseEndDate)) {
                getEditPaymentInfo(licensesInfo.clinicId);
                setEditInfoBar(true);
            }
            else{
                setEditInfoBar(false);
            }
        }
    }, [licensesInfo, getEditPaymentInfo]);

    useEffect(() => {
        if(error !== ''){
            logger.logError(`Failed to fetch the edit payment method details`)
            handleUserLicenseUpdate(error, InfoMessageType.error);
        }
    }, [error, handleUserLicenseUpdate])

    return (
        <>
            {(pageLoader || storageLoader) && <Loader />}
            {infoBar && (
                <div className="row mt-3" style={{ paddingLeft: '1vw', zIndex: 1002 }}>
                    <InfoBar
                        Message={msg.current}
                        MessageType={msgType.current}
                        closeClick={() => {
                            setInfoBar(false);
                        }}
                    />
                </div>
            )}

            {showPaymentOption && editInfoBar && (
                <div className="row mt-3" style={{ paddingLeft: '1vw', zIndex: 1001 }}>
                    <InfoBar
                        Message={t('AuraSubscriptionPaused')}
                        MessageType={InfoMessageType.error}
                        linkText={t('EditPaymentMethod')}
                        closeClick={() => setEditInfoBar(false)}
                        onClickLink={handleClickLink}
                    />
                </div>
            )}
          
            {showPaymentSuccess &&
                <PaymentSuccess onClose={() => closePaymentSuccessMessage()} />} 
            {
               !isPaymentSupoortEnabled() && licensesInfo && (<div className="payment-support-container">
                <LicenseTitle
                            licensesInfo={licensesInfo}
                            isSuiteLicense={isSuiteLicense()}
                            showPaymentOption={showPaymentOption}
                            containsSuiteLicense={containsSuiteLicense()} />
                </div>)
            }

            {
                isPaymentSupoortEnabled() && licensesInfo && (
                    <>
                    <div className="payment-support-container">
                        <PaymentSupport
                            isSuiteLicense={isSuiteLicense()}
                            showPaymentOption={showPaymentOption}
                            containsSuiteLicense={containsSuiteLicense()}
                            clinicId={licensesInfo?.clinicId}
                            setInfo={handleUserLicenseUpdate}
                            updatePageLoader={setPageLoader}
                            licensesInfo={licensesInfo}
                            clinicName={clinicInfo?.name || ''}
                            isClinicAdmin={currentUser?.isClinicAdmin || false}
                            isPermanent = {isPermanentLicense()} />
                    </div>
                    </>
                )
            }

            {/* Upgrade for Non Suite License */}
           
            {
                isSubscriptionLicense() && (
                    <div className="payment-support-container">
                        <UpgradeNonSuite/>
                    </div>
                )
            }

            {  
               (clinicHasUserAndStorageLicenses() || clinicHasStorageLicense()) && licensesInfo?.clinicId &&
               <div className={`${isSuiteLicense() ? '' : 'mt-2'} `}>
                        <CloudStorage
                            clinicId={licensesInfo?.clinicId}
                            setInfo={handleCloudStorageUpdate}
                            updatePageLoader={setStorageLoader}
                            licensesInfo={licensesInfo}
                            isSuiteLicense={isSuiteLicense()}
                            showPaymentOption={showPaymentOption}
                            containsSuiteLicense={containsSuiteLicense()}
                        />
                </div>
            }

            {
                !(clinicHasUserAndStorageLicenses() || clinicHasUserLicense()) && licensesInfo &&
                  <NoLicenseActivated 
                   clinicId={licensesInfo.clinicId}
                   updatePageLoader={ setPageLoader}
                   setInfo={handleUserLicenseUpdate}
                   showPaymentOption={showPaymentOption}
                   isClinicAdmin={currentUser?.isClinicAdmin || false}
                    />
            }
            {
                (clinicHasUserAndStorageLicenses() && !clinicHasUserLicense() && licensesInfo?.clinicId  &&
                    <>
                        <div className="mt-3">
                            <Licenses
                                clinicId={licensesInfo.clinicId}
                                setInfo={handleUserLicenseUpdate}
                                updatePageLoader={setPageLoader}
                                licensesInfo={licensesInfo}
                                isSuiteLicense={isSuiteLicense()}/>
                        </div>
                    </>
                )
            }
            {/* For Old Licenses */}
            {
                (!clinicHasUserAndStorageLicenses() && clinicHasUserLicense() && licensesInfo?.clinicId  &&
                    <>
                        <div className="mt-3">
                            <LicensesUser
                                clinicId={licensesInfo.clinicId}
                                setInfo={handleUserLicenseUpdate}
                                updatePageLoader={setPageLoader}
                                licensesInfo={licensesInfo}
                                isSuiteLicense={isSuiteLicense()}/>
                        </div>
                    </>
                )
            }
        </>
    )
}
export default LicensesAndStorage